import React, { useState, useEffect } from 'react';
import { _T, C } from '../../shared/theme';
import { getUserBadges } from '../../shared/dbService';

// ─── Badge definitions ────────────────────────────────────────────────────────
const BADGE_DEFS = {
  stability: {
    silver: {
      icon: '⚖️',
      labelNL: 'Evenwichtig',
      labelEN: 'Balanced',
      descNL: 'Heeft een consistente slaap, stemming en oefeningsroutine.',
      descEN: 'Maintains consistent sleep, mood and exercise routines.',
      color: '#5B9ED0',
      glow: '#5B9ED044',
    },
    gold: {
      icon: '🏛️',
      labelNL: 'Stabiel Persoon',
      labelEN: 'Stable Person',
      descNL: 'Uitzonderlijk gebalanceerd in welzijn en zelfzorg.',
      descEN: 'Exceptionally balanced in wellbeing and self-care.',
      color: '#C8A84B',
      glow: '#C8A84B55',
    },
  },
  social: {
    silver: {
      icon: '🌐',
      labelNL: 'Sociaal',
      labelEN: 'Social',
      descNL: 'Actief in de community — reageert, deelt en oefent samen.',
      descEN: 'Active in the community — reacts, shares and exercises together.',
      color: '#2A9E82',
      glow: '#2A9E8244',
    },
    gold: {
      icon: '💫',
      labelNL: 'Zeer Sociaal',
      labelEN: 'Very Social',
      descNL: 'Sterke sociale aanwezigheid en diepe banden met anderen.',
      descEN: 'Strong social presence and deep bonds with others.',
      color: '#7B6FD0',
      glow: '#7B6FD055',
    },
  },
  gamer: {
    bronze: {
      icon: '🎮',
      labelNL: 'Speler',
      labelEN: 'Gamer',
      descNL: 'Eerste hersenspel gespeeld. Goed begin!',
      descEN: 'Played your first brain game. Good start!',
      color: '#8B7355',
      glow: '#8B735544',
    },
    silver: {
      icon: '🎮',
      labelNL: 'Vaste Speler',
      labelEN: 'Regular Gamer',
      descNL: '10+ hersenspellen gespeeld. Jij traint niet alleen je lichaam.',
      descEN: 'Played 10+ brain games. You train your mind, not just your body.',
      color: '#7B6FD0',
      glow: '#7B6FD044',
    },
    gold: {
      icon: '🏆',
      labelNL: 'Game Master',
      labelEN: 'Game Master',
      descNL: '50+ hersenspellen voltooid. Een echte mentale atleet.',
      descEN: '50+ brain games completed. A true mental athlete.',
      color: '#C8A84B',
      glow: '#C8A84B55',
    },
  },
  athlete: {
    bronze: {
      icon: '💪',
      labelNL: 'Beginner',
      labelEN: 'Beginner',
      descNL: 'Eerste oefensessie voltooid. De eerste stap is gezet.',
      descEN: 'Completed your first exercise session. First step taken.',
      color: '#8B7355',
      glow: '#8B735544',
    },
    silver: {
      icon: '💪',
      labelNL: 'Actief',
      labelEN: 'Active',
      descNL: '10+ oefensessies voltooid. Consistent bewegen loont.',
      descEN: '10+ exercise sessions completed. Consistent movement pays off.',
      color: '#4AC87A',
      glow: '#4AC87A44',
    },
    gold: {
      icon: '🔥',
      labelNL: 'Atleet',
      labelEN: 'Athlete',
      descNL: '50+ oefensessies voltooid. Jij bent de definitie van discipline.',
      descEN: '50+ exercise sessions completed. You are the definition of discipline.',
      color: '#D4873A',
      glow: '#D4873A55',
    },
  },
  grower: {
    silver: {
      icon: '🌱',
      labelNL: 'Groeier',
      labelEN: 'Grower',
      descNL: '5+ programmataken of trainingsmodules voltooid.',
      descEN: '5+ program tasks or training modules completed.',
      color: '#2A9E82',
      glow: '#2A9E8244',
    },
    gold: {
      icon: '🦋',
      labelNL: 'Transformeerder',
      labelEN: 'Transformer',
      descNL: '20+ groeiactiviteiten voltooid. Persoonlijke groei is jouw pad.',
      descEN: '20+ growth activities completed. Personal growth is your path.',
      color: '#C8A84B',
      glow: '#C8A84B55',
    },
  },
  entrepreneur: {
    gold: {
      icon: '🏢',
      labelNL: 'Ondernemer',
      labelEN: 'Entrepreneur',
      descNL: 'Ondernemend geest — eigenaar of lid van een bedrijf.',
      descEN: 'Entrepreneurial spirit — owner or member of a business.',
      color: '#C8A84B',
      glow: '#C8A84B55',
    },
  },
  coach: {
    gold: {
      icon: '🎓',
      labelNL: 'Coach',
      labelEN: 'Coach',
      descNL: 'Gecertificeerde coach op het Sense Lifting platform.',
      descEN: 'Certified coach on the Sense Lifting platform.',
      color: '#7B6FD0',
      glow: '#7B6FD055',
    },
  },
  streak: {
    bronze: {
      icon: '🔥',
      labelNL: '3-Daagse Reeks',
      labelEN: '3-Day Streak',
      descNL: '3 opeenvolgende dagen ingelogd en gemonitord.',
      descEN: '3 consecutive days of tracking sleep or mood.',
      color: '#D4873A',
      glow: '#D4873A44',
    },
    silver: {
      icon: '🔥',
      labelNL: 'Week Strijder',
      labelEN: 'Week Warrior',
      descNL: '7 dagen op rij slaap of stemming gelogd.',
      descEN: '7 days in a row of sleep or mood tracking.',
      color: '#5B9ED0',
      glow: '#5B9ED044',
    },
    gold: {
      icon: '⚡',
      labelNL: 'Maand Kampioen',
      labelEN: 'Month Champion',
      descNL: '30 dagen aaneengesloten bijgehouden. IJzersterk.',
      descEN: '30 consecutive days tracked. Unbreakable.',
      color: '#C8A84B',
      glow: '#C8A84B55',
    },
  },
};

// ─── Local badge computation from localStorage ───────────────────────────────
function getLocalBadges() {
  try {
    // Games: count unique completed game sessions
    let gameCount = 0;
    try { gameCount = JSON.parse(localStorage.getItem('sl_game_scores') || '[]').length; } catch {}

    // Exercises: count from history
    let exCount = 0;
    try { exCount = JSON.parse(localStorage.getItem('sl_ex_history') || '[]').length; } catch {}

    // Programs + training: count activity log entries across all business keys
    let activityCount = 0;
    try {
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('sl_biz_activity_') || k === 'sl_biz_training_done') {
          if (k === 'sl_biz_training_done') {
            activityCount += JSON.parse(localStorage.getItem(k) || '[]').length;
          } else {
            activityCount += JSON.parse(localStorage.getItem(k) || '[]').length;
          }
        }
      });
    } catch {}

    const gamerLevel = gameCount >= 20 ? 'gold' : gameCount >= 5 ? 'silver' : null;
    const athleteLevel = exCount >= 30 ? 'gold' : exCount >= 10 ? 'silver' : null;
    const growerLevel = activityCount >= 20 ? 'gold' : activityCount >= 5 ? 'silver' : null;

    return { gameCount, exCount, activityCount, gamerLevel, athleteLevel, growerLevel };
  } catch {
    return { gameCount: 0, exCount: 0, activityCount: 0, gamerLevel: null, athleteLevel: null, growerLevel: null };
  }
}

// Locked (not yet earned) badge stubs
const LOCKED_DEFS = {
  stability: {
    icon: '⚖️',
    labelNL: 'Evenwichtig',
    labelEN: 'Balanced',
    hintNL: 'Log slaap, stemming en oefeningen gedurende 2+ weken',
    hintEN: 'Log sleep, mood and exercises for 2+ weeks consistently',
  },
  social: {
    icon: '🌐',
    labelNL: 'Sociaal',
    labelEN: 'Social',
    hintNL: 'Reageer op posts en doe oefeningen samen met anderen',
    hintEN: 'React to posts and exercise together with others',
  },
  gamer: {
    icon: '🎮',
    labelNL: 'Speler',
    labelEN: 'Gamer',
    hintNL: 'Speel 5+ hersenspellen om dit badge te verdienen',
    hintEN: 'Play 5+ brain games to earn this badge',
  },
  athlete: {
    icon: '💪',
    labelNL: 'Actief',
    labelEN: 'Active',
    hintNL: 'Voltooi 10+ oefensessies om dit badge te verdienen',
    hintEN: 'Complete 10+ exercise sessions to earn this badge',
  },
  grower: {
    icon: '🌱',
    labelNL: 'Groeier',
    labelEN: 'Grower',
    hintNL: 'Voltooi 5+ programmataken of trainingsmodules',
    hintEN: 'Complete 5+ program tasks or training modules',
  },
  streak: {
    icon: '🔥',
    labelNL: 'Reeks',
    labelEN: 'Streak',
    hintNL: 'Log 3 opeenvolgende dagen slaap of stemming',
    hintEN: 'Log sleep or mood for 3 consecutive days',
  },
};

function BadgeChip({ badgeDef, nl, animate = false }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '12px 14px',
        borderRadius: 16,
        background: `${badgeDef.glow}`,
        border: `1.5px solid ${badgeDef.color}55`,
        boxShadow: hovered ? `0 0 18px ${badgeDef.glow}, 0 2px 8px rgba(0,0,0,0.3)` : `0 0 10px ${badgeDef.glow}`,
        cursor: 'default',
        transition: 'box-shadow 0.3s ease',
        minWidth: 90,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Shimmer overlay on hover */}
      {hovered && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, transparent 30%, ${badgeDef.color}18 50%, transparent 70%)`,
          pointerEvents: 'none',
        }}/>
      )}

      <div style={{ fontSize: 28, lineHeight: 1, filter: `drop-shadow(0 0 6px ${badgeDef.color}88)` }}>
        {badgeDef.icon}
      </div>
      <div style={{ fontSize: 10, color: badgeDef.color, fontWeight: 700, letterSpacing: 0.5, textAlign: 'center', lineHeight: 1.2 }}>
        {nl ? badgeDef.labelNL : badgeDef.labelEN}
      </div>

      {/* Tooltip on hover */}
      {hovered && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#0e1828',
          border: `1px solid ${badgeDef.color}44`,
          borderRadius: 10,
          padding: '8px 12px',
          fontSize: 11,
          color: C.silver2,
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
          zIndex: 100,
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
        }}>
          {nl ? badgeDef.descNL : badgeDef.descEN}
          {/* Arrow */}
          <div style={{
            position: 'absolute', bottom: -5, left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: `5px solid ${badgeDef.color}44`,
          }}/>
        </div>
      )}
    </div>
  );
}

function LockedBadgeChip({ def, nl }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
        gap: 6, padding: '12px 14px', borderRadius: 16,
        background: `${C.silver}08`, border: `1.5px solid ${C.silver}18`,
        cursor: 'default', minWidth: 90, position: 'relative',
        opacity: 0.5,
      }}
    >
      <div style={{ fontSize: 28, lineHeight: 1, filter: 'grayscale(1) opacity(0.4)' }}>
        {def.icon}
      </div>
      <div style={{ fontSize: 10, color: C.silver, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
        {nl ? def.labelNL : def.labelEN}
      </div>
      <div style={{
        position: 'absolute', top: 4, right: 6,
        fontSize: 10, color: `${C.silver}60`,
      }}>🔒</div>

      {hovered && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
          transform: 'translateX(-50%)',
          background: '#0e1828', border: `1px solid ${C.silver}22`,
          borderRadius: 10, padding: '8px 12px',
          fontSize: 11, color: C.silver, lineHeight: 1.5,
          whiteSpace: 'normal', zIndex: 100,
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          pointerEvents: 'none', maxWidth: 200, textAlign: 'center',
        }}>
          {nl ? def.hintNL : def.hintEN}
        </div>
      )}
    </div>
  );
}

function ProfileBadges({ userId, lang, isOwnProfile = false }) {
  const nl = lang === 'nl';
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    getUserBadges(userId).then(data => {
      setBadges(data);
      setLoading(false);
    });
  }, [userId]);

  if (loading) return null;

  const stabilityLevel   = badges?.stability?.badge    || null;
  const socialLevel      = badges?.social?.badge       || null;
  const streakLevel      = badges?.streak?.badge       || null;
  const entrepreneurLevel= badges?.entrepreneur?.badge || null;
  const coachLevel       = badges?.coach?.badge        || null;

  // Use server-side game/exercise data if available, fall back to localStorage for own profile
  const local = isOwnProfile ? getLocalBadges() : { gamerLevel: null, athleteLevel: null, growerLevel: null, gameCount: 0, exCount: 0, activityCount: 0 };
  // Prefer server-side badge data when available
  const gamerLevel   = badges?.game?.badge     || local.gamerLevel;
  const athleteLevel = badges?.exercise?.badge || local.athleteLevel;
  const { growerLevel, gameCount, exCount, activityCount } = local;
  const serverGameCount = badges?.game?.total_games ?? gameCount;
  const serverExCount   = badges?.exercise?.total_exercises ?? exCount;

  const hasAny = stabilityLevel || socialLevel || gamerLevel || athleteLevel || growerLevel || streakLevel || entrepreneurLevel || coachLevel;

  const showLocked = isOwnProfile;

  if (!hasAny && !showLocked) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>
        BADGES
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {stabilityLevel
          ? <BadgeChip badgeDef={BADGE_DEFS.stability[stabilityLevel]} nl={nl} />
          : showLocked && <LockedBadgeChip def={LOCKED_DEFS.stability} nl={nl} />}

        {socialLevel
          ? <BadgeChip badgeDef={BADGE_DEFS.social[socialLevel]} nl={nl} />
          : showLocked && <LockedBadgeChip def={LOCKED_DEFS.social} nl={nl} />}

        {gamerLevel
          ? <BadgeChip badgeDef={BADGE_DEFS.gamer[gamerLevel]} nl={nl} />
          : showLocked && <LockedBadgeChip def={LOCKED_DEFS.gamer} nl={nl} />}

        {athleteLevel
          ? <BadgeChip badgeDef={BADGE_DEFS.athlete[athleteLevel]} nl={nl} />
          : showLocked && <LockedBadgeChip def={LOCKED_DEFS.athlete} nl={nl} />}

        {growerLevel
          ? <BadgeChip badgeDef={BADGE_DEFS.grower[growerLevel]} nl={nl} />
          : showLocked && <LockedBadgeChip def={LOCKED_DEFS.grower} nl={nl} />}

        {streakLevel
          ? <BadgeChip badgeDef={BADGE_DEFS.streak[streakLevel]} nl={nl} />
          : showLocked && <LockedBadgeChip def={LOCKED_DEFS.streak} nl={nl} />}

        {entrepreneurLevel && <BadgeChip badgeDef={BADGE_DEFS.entrepreneur[entrepreneurLevel]} nl={nl} />}
        {coachLevel        && <BadgeChip badgeDef={BADGE_DEFS.coach[coachLevel]}        nl={nl} />}
      </div>

      {/* Own profile progress hints */}
      {isOwnProfile && (
        <div style={{ marginTop: 10, fontSize: 11, color: C.silver, lineHeight: 1.8, fontStyle: 'italic' }}>
          {!stabilityLevel && <div>⚖️ {nl ? `Nog ${Math.max(0, 45 - (badges?.stability?.score || 0))} punten voor evenwicht` : `${Math.max(0, 45 - (badges?.stability?.score || 0))} pts for Balanced`}</div>}
          {!socialLevel    && <div>🌐 {nl ? `Nog ${Math.max(0, 35 - (badges?.social?.score || 0))} punten voor sociaal` : `${Math.max(0, 35 - (badges?.social?.score || 0))} pts for Social`}</div>}
          {!gamerLevel     && <div>🎮 {nl ? `${serverGameCount}/10 spellen gespeeld` : `${serverGameCount}/10 games played`}</div>}
          {!athleteLevel   && <div>💪 {nl ? `${serverExCount}/10 oefensessies` : `${serverExCount}/10 exercise sessions`}</div>}
          {!growerLevel    && <div>🌱 {nl ? `${activityCount}/5 groeiactiviteiten` : `${activityCount}/5 growth activities`}</div>}
          {!streakLevel    && <div>🔥 {nl ? `${badges?.streak?.max_streak || 0}/3 opeenvolgende dagen` : `${badges?.streak?.max_streak || 0}/3 consecutive days`}</div>}
        </div>
      )}
    </div>
  );
}

export default ProfileBadges;
